import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Headline, TextLink } from '../../../../design-system';
import { useSelector } from 'react-redux';
import {
  CpoLabelWrapper,
  DagLabel,
  ImageDiscalimer,
  ImageWrapper,
  ImageWrapperAlpha,
  NameAndTrimWrapper,
  OfferInfo,
  PriceWrapper,
  PriceDisclaimer,
  SuperScript,
  TagAndDagWrapper,
  TileWrapper,
  VehicleImage,
  VehicleInfo,
  WhyBuy,
  Price,
} from './VehicleTileCa.styles';
import { TagLabel } from '../../Common/Common.styles';
import { PromotionType } from '../../../types/offers';
import CpoLabel from '../../../assets/icons/CpoLabel';
import PricingOptionsCa from './PricingOptionsCa';
import VehicleTileCTAs from '../VehicleTile/VehicleTileCTAs';

import useCtaUrls from '../../../hooks/useCtaUrls';
import useShowDetails from '../../../hooks/useShowDetails';
import { numberFormatter } from '../../../utils/formatPrice';
import Modal from '../../Modal';
import { selectDealerContextId } from '../../../reducers/dealerContextReducer';
import { parseDigitalLeadsData } from '../../../utils/digitalLeadsFormat';
import { selectZipCode } from '../../../reducers/zipCodeReducer';

interface VehicleTileCaProps {
  offer: PromotionType;
}

const VehicleTileCa: React.FC<VehicleTileCaProps> = ({ offer }) => {
  const isAlpha = !process.env.IS_LEGACY;
  const { t, i18n } = useTranslation();

  const lang = i18n.language;
  const isFrenchPage = lang === 'fr';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const shouldAutoOpen = useShowDetails();
  useEffect(() => {
    // URL filter params are applied to Redux asynchronously, so shouldAutoOpen
    // can transition false→true after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (shouldAutoOpen) setIsModalOpen(true);
  }, [shouldAutoOpen]);

  const { dealerId } = useSelector(selectDealerContextId);

  const { market } = useSelector(selectZipCode);

  const {
    offerType,
    offerModelDetail,
    offerImageUrl,
    term,
    programToDate,
    customerDownPayment,
    monthlyPayment,
    aprRate,
  } = offer;

  const { dag, vehicleType, modelYear, modelName, buildAudiUrlEn, buildAudiUrlFr, msrp, trimline } =
    offerModelDetail;

  const inventoryUrl = useCtaUrls(offer);

  const isCpoOffer = vehicleType === 'USED-CAR';
  const isCaMarket = market === 'CA';

  const buildYourAudiUrl = isFrenchPage ? buildAudiUrlFr : buildAudiUrlEn;

  const digitalLeadsInfo = useMemo(() => {
    // Passing empty string because fro CA we don't need ZipCode
    const dataInfo = parseDigitalLeadsData(offer, isCaMarket, '', dealerId);
    return dataInfo;
  }, [offer, dealerId, isCaMarket]);

  const disabledCta = !isCpoOffer && !buildYourAudiUrl;

  const href = useMemo(() => {
    if (disabledCta) return 'javascript:void(0)';
    if (isCpoOffer) return `https://www.audi.ca/${lang}/cpo/layer/Why-buy-certified/`;
    return buildYourAudiUrl;
  }, [buildYourAudiUrl, disabledCta, isCpoOffer, lang]);

  return (
    <>
      <TileWrapper
        isCpoOffer={isCpoOffer}
        isAlpha={isAlpha}
        data-promotion-type={offerType} // either lease, finane, or credit
        data-promotion-offerdate={programToDate} // validity date of offer
        data-promotion-credit={offer.creditBonus} // credit bonus offer // ??
        data-promotion-lease-downpayment={customerDownPayment?.toString() ?? 'null'} // downpayment for lease offer
        data-promotion-lease-payment={monthlyPayment?.toString() ?? 'null'} // monthly payment for lease offer
        data-promotion-lease-termlength={offerType === 'lease' ? term : 'null'} // term length for lease offer
        data-promotion-finance-apr={offerType === 'finance' ? aprRate : 'null'} // apr for finance offer
        data-promotion-finance-termlength={offerType === 'finance' ? term : 'null'} // termlength for finance offer
        data-vehicle-model-name={modelName} // name of model -excludes year // ?
        data-vehicle-model-year={modelYear} // year of model
        data-vehicle-price={msrp !== 0 ? msrp : 'null'} // price of vehicle // ?
        data-vehicle-trim={trimline} // vehicle trim
        data-testid={`offer-tile-${vehicleType}-${dag}`}
      >
        <TagAndDagWrapper>
          <TagLabel data-testid={`${offerType}-tag`}>
            {t(`promo.tile.offerType.${offerType}`)}
          </TagLabel>
          <DagLabel>
            <Text
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t(`promo.tile.dag.${dag}`)}
            </Text>
          </DagLabel>
        </TagAndDagWrapper>
        {isAlpha ? (
          <ImageWrapperAlpha
            data-testid="offer-hero-image"
            imageUrl={offerImageUrl ?? undefined}
            isCpoOffer={isCpoOffer}
          />
        ) : (
          <ImageWrapper isCpoOffer={isCpoOffer}>
            <VehicleImage
              data-testid="offer-hero-image"
              src={offerImageUrl}
              alt="vehicle-sample-img"
            />
            {isCpoOffer && <ImageDiscalimer>{t('promo.tile.imageReference')}</ImageDiscalimer>}
          </ImageWrapper>
        )}
        {isCpoOffer && (
          <CpoLabelWrapper>
            <CpoLabel />
          </CpoLabelWrapper>
        )}
        <NameAndTrimWrapper>
          <Headline
            data-testid="offer-name"
            tag="h3"
            variant={{
              order: '3',
              type: 'headline',
              weight: 'bold',
            }}
          >
            {modelName}
          </Headline>
          {!isCpoOffer && (
            <Text
              variant={{
                order: '1',
                style: 'normal',
              }}
            >
              {trimline}
            </Text>
          )}
        </NameAndTrimWrapper>
        {!isCpoOffer && (
          <PriceWrapper>
            <Text
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t(`promo.tile.startingAt`)}
            </Text>
            <Price>
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {numberFormatter(msrp, lang)}
              </Headline>
              <SuperScript>
                <TextLink
                  className="link"
                  onClick={() => setIsModalOpen(!isModalOpen)}
                  size="sm"
                  variant="inline"
                >
                  1
                </TextLink>
              </SuperScript>
            </Price>
          </PriceWrapper>
        )}
        <VehicleInfo>
          <OfferInfo isCpoOffer={isCpoOffer}>
            <PricingOptionsCa offer={offer} showOfferDetails={() => setIsModalOpen(true)} />
          </OfferInfo>
        </VehicleInfo>
        <VehicleTileCTAs inventoryUrl={inventoryUrl} digitalLeadsInfo={digitalLeadsInfo} />
        {isCpoOffer && <PriceDisclaimer>{t('promo.tile.priceDisclaimer')}</PriceDisclaimer>}
        <WhyBuy disabledCta={disabledCta} isAlpha={isAlpha}>
          {/* @ts-ignore */}
          <TextLink
            data-testid="why-buy-cpo-cta"
            href={href}
            target="_blank"
            variant="standalone"
            size="md"
            icon="external-link"
            iconPosition="trailing"
          >
            {isCpoOffer ? t('promo.tile.whyBuyCpo') : t('promo.tile.buildAudi')}
          </TextLink>
        </WhyBuy>
      </TileWrapper>
      {isModalOpen && (
        <Modal
          offer={offer}
          inventoryUrl={inventoryUrl}
          onCloseModal={() => setIsModalOpen(false)}
          dealerId={dealerId}
          isModal
        />
      )}
    </>
  );
};

export default VehicleTileCa;
